import { useQuery } from '@tanstack/react-query';
import { ApiService } from 'api';
import { isRequestedFileFulfilled } from 'common/types/files';
import { useFirmDocumentMeasurementDateDocumentsQuery } from 'services/hooks/documents';

type UseOperationsDocumentCountsParams = {
  enabled?: boolean;
  cmdId?: number | null;
  companyId?: number | null;
  measurementDateId?: number | null;
};

export type OperationsDocumentCounts = {
  // Requested documents and questions still outstanding on the active request.
  pendingRequestedCount: number | null;
  // Documents stored against the valuation measurement date.
  uploadedDocumentCount: number | null;
};

// Counts shown as tickers on the Operations summary buttons so analysts can see
// outstanding request items and uploaded documents without opening a modal.
export const useOperationsDocumentCounts = ({
  enabled = true,
  cmdId,
  companyId,
  measurementDateId,
}: UseOperationsDocumentCountsParams): OperationsDocumentCounts => {
  const { data: requestStatus } = useQuery({
    queryKey: ['operations-information-request-status', cmdId],
    queryFn: async () => {
      const response = await ApiService.apiRetrieveInformationRequestStatusRead(String(cmdId));
      return response.results[0] ?? null;
    },
    enabled: enabled && Boolean(cmdId),
  });

  const { data: measurementDateDocuments } = useFirmDocumentMeasurementDateDocumentsQuery({
    companyId: companyId ?? null,
    enabled: enabled && Boolean(companyId && measurementDateId),
    measurementDateId: measurementDateId ?? null,
  });

  const pendingFiles = (requestStatus?.files ?? []).filter(
    file => !isRequestedFileFulfilled(file)
  ).length;
  const pendingQuestions = (requestStatus?.questions ?? []).filter(
    question => !question.answered
  ).length;

  return {
    pendingRequestedCount: requestStatus ? pendingFiles + pendingQuestions : null,
    uploadedDocumentCount: measurementDateDocuments
      ? (measurementDateDocuments.files?.length ?? 0)
      : null,
  };
};

export default useOperationsDocumentCounts;
